import React, { useState, useCallback } from 'react';
import {
  Sparkles, Loader2, X, AlertCircle, Wand2,
  FileCode, CheckCircle2, ChevronDown, ChevronUp,
} from 'lucide-react';
import { generateWorkflowAI } from '../../lib/workflowApi';
import { motion, AnimatePresence } from 'framer-motion';

// ── Example prompts shown as quick-start suggestions ──────────────────────
const EXAMPLE_PROMPTS = [
  'Every morning fetch customer reviews, analyze sentiment, and notify Slack if negative.',
  'When a webhook fires, extract the payload, run an HTTP POST to our CRM, then send a confirmation email.',
  'Daily: query our PostgreSQL database for overdue invoices and send a summary email to the finance team.',
];

// ── Sub-component: Plan steps ──────────────────────────────────────────────
function PlanView({ plan }) {
  const steps = plan
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {steps.map((step, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div
            style={{
              minWidth: 20, height: 20, borderRadius: '50%',
              background: 'linear-gradient(135deg,#8B5CF6,#6366F1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0,
            }}
          >
            {i + 1}
          </div>
          <span style={{ fontSize: 12, lineHeight: 1.5 }}>{step.replace(/^\d+\.\s*/, '')}</span>
        </div>
      ))}
    </div>
  );
}

// ── Sub-component: DSL viewer ──────────────────────────────────────────────
function DSLView({ dsl, isDark }) {
  return (
    <pre
      style={{
        margin: 0, fontSize: 11, lineHeight: 1.6,
        color: isDark ? '#94A3B8' : '#475569',
        overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        maxHeight: 300, overflowY: 'auto',
      }}
    >
      {dsl}
    </pre>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function AIWorkflowGenerator({ onGenerate, onClose, isDark }) {
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);     // GenerationResponse
  const [activeTab, setActiveTab] = useState('plan'); // 'plan' | 'dsl'

  const bg = isDark ? 'rgba(13,17,26,0.97)' : 'rgba(255,255,255,0.97)';
  const border = isDark ? '#1F2937' : 'rgba(0,0,0,0.08)';
  const textPrimary = isDark ? '#F8FAFC' : '#1E293B';
  const textSecondary = isDark ? '#94A3B8' : '#64748B';
  const inputBg = isDark ? '#0D1117' : '#F8FAFC';
  const inputBorder = isDark ? '#30363D' : '#E2E8F0';
  const cardBg = isDark ? '#161B22' : '#F1F5F9';
  const cardBorder = isDark ? '#30363D' : '#E2E8F0';

  const handleGenerate = useCallback(async () => {
    const trimmed = prompt.trim();
    if (!trimmed || trimmed.length < 10) {
      setError('Please enter a more detailed description (at least 10 characters).');
      return;
    }
    setGenerating(true);
    setError(null);
    setResult(null);

    try {
      const res = await generateWorkflowAI(trimmed);
      const data = res.data;

      if (!data.success) {
        setError(data.error || 'Generation failed. Please try again.');
        return;
      }

      setResult(data);
      // Immediately push nodes to the canvas
      onGenerate(data);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        'Network error. Please check your connection.';
      setError(msg);
    } finally {
      setGenerating(false);
    }
  }, [prompt, onGenerate]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleGenerate();
    }
  };

  return (
    <motion.div
      key="ai-panel"
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 420, opacity: 0 }}
      transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      style={{
        width: 390,
        height: '100%',
        background: bg,
        borderLeft: `1px solid ${border}`,
        backdropFilter: 'blur(20px)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-12px 0 40px rgba(0,0,0,0.15)',
        overflowY: 'auto',
        flexShrink: 0,
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div
        style={{
          padding: '18px 20px',
          borderBottom: `1px solid ${border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: isDark
            ? 'linear-gradient(135deg,rgba(139,92,246,0.08),rgba(99,102,241,0.04))'
            : 'linear-gradient(135deg,rgba(139,92,246,0.05),rgba(99,102,241,0.02))',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg,#8B5CF6,#6366F1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(139,92,246,0.35)',
            }}
          >
            <Sparkles size={18} color="#fff" />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: textPrimary }}>
              AI Workflow Generator
            </h3>
            <span style={{ fontSize: 11, color: textSecondary }}>
              Natural Language → Canvas
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          title="Close"
          style={{
            background: isDark ? '#1F2937' : '#F1F5F9',
            border: `1px solid ${border}`,
            color: textSecondary,
            cursor: 'pointer',
            borderRadius: 8,
            padding: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.15s',
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
        {/* Prompt area */}
        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="ai-prompt"
            style={{
              display: 'block', fontSize: 11, fontWeight: 600,
              color: textSecondary, marginBottom: 8, letterSpacing: '0.04em',
            }}
          >
            DESCRIBE YOUR WORKFLOW
          </label>
          <textarea
            id="ai-prompt"
            value={prompt}
            onChange={(e) => { setPrompt(e.target.value); setError(null); }}
            onKeyDown={handleKeyDown}
            placeholder="e.g. Every morning fetch customer data from our API, analyze sentiment of reviews, and notify Slack if any are negative."
            rows={5}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '12px 14px',
              borderRadius: 12,
              border: `1.5px solid ${error ? '#EF4444' : inputBorder}`,
              background: inputBg,
              color: textPrimary,
              fontSize: 13, lineHeight: 1.6, resize: 'vertical',
              outline: 'none', fontFamily: 'inherit',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => { if (!error) e.target.style.borderColor = '#8B5CF6'; }}
            onBlur={(e) => { if (!error) e.target.style.borderColor = inputBorder; }}
          />
          <p style={{ margin: '6px 0 0', fontSize: 11, color: textSecondary }}>
            Tip: Ctrl+Enter to generate · Be specific about triggers and actions.
          </p>
        </div>

        {/* Example prompts */}
        <div style={{ marginBottom: 16 }}>
          <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: textSecondary, letterSpacing: '0.04em' }}>
            QUICK EXAMPLES
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {EXAMPLE_PROMPTS.map((ex, i) => (
              <button
                key={i}
                onClick={() => { setPrompt(ex); setError(null); setResult(null); }}
                style={{
                  textAlign: 'left', padding: '8px 12px', borderRadius: 8,
                  background: cardBg, border: `1px solid ${cardBorder}`,
                  color: textSecondary, fontSize: 11, lineHeight: 1.4,
                  cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#8B5CF6';
                  e.currentTarget.style.color = textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = cardBorder;
                  e.currentTarget.style.color = textSecondary;
                }}
              >
                {ex}
              </button>
            ))}
          </div>
        </div>

        {/* Generate button */}
        <button
          id="ai-generate-btn"
          onClick={handleGenerate}
          disabled={generating || prompt.trim().length < 10}
          style={{
            width: '100%', padding: '12px 16px', borderRadius: 12,
            background:
              generating || prompt.trim().length < 10
                ? (isDark ? '#1F2937' : '#E2E8F0')
                : 'linear-gradient(135deg,#8B5CF6,#6366F1)',
            color: generating || prompt.trim().length < 10
              ? (isDark ? '#374151' : '#94A3B8')
              : '#fff',
            border: 'none', fontWeight: 700, fontSize: 14,
            cursor: generating || prompt.trim().length < 10 ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            boxShadow: generating || prompt.trim().length < 10
              ? 'none' : '0 4px 16px rgba(139,92,246,0.3)',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => {
            if (!generating && prompt.trim().length >= 10) {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(139,92,246,0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = generating || prompt.trim().length < 10
              ? 'none' : '0 4px 16px rgba(139,92,246,0.3)';
          }}
        >
          {generating ? <Loader2 size={18} className="spin" /> : <Wand2 size={18} />}
          {generating ? 'Generating Workflow…' : 'Generate Workflow'}
        </button>

        {/* Error state */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              style={{
                marginTop: 14, padding: '12px 14px', borderRadius: 10,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                display: 'flex', gap: 10, alignItems: 'flex-start',
              }}
            >
              <AlertCircle size={16} color="#EF4444" style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 12, color: '#EF4444', lineHeight: 1.5 }}>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Success result */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              {/* Success banner */}
              <div
                style={{
                  padding: '10px 14px', borderRadius: 10,
                  background: 'rgba(16,185,129,0.08)',
                  border: '1px solid rgba(16,185,129,0.25)',
                  display: 'flex', gap: 10, alignItems: 'center',
                }}
              >
                <CheckCircle2 size={16} color="#10B981" style={{ flexShrink: 0 }} />
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#10B981' }}>
                    Workflow generated successfully!
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: textSecondary }}>
                    {result.nodes.length} nodes · {result.edges.length} edges · Rendered on canvas
                  </p>
                </div>
              </div>

              {/* Intent */}
              {result.intent && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: cardBg, border: `1px solid ${cardBorder}` }}>
                  <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, color: textSecondary, letterSpacing: '0.06em' }}>
                    INTENT
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: textPrimary, lineHeight: 1.5 }}>{result.intent}</p>
                </div>
              )}

              {/* Tabs: Plan / DSL */}
              <div>
                <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                  {['plan', 'dsl'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      style={{
                        padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        border: '1px solid ' + (activeTab === tab ? '#8B5CF6' : cardBorder),
                        background: activeTab === tab
                          ? 'rgba(139,92,246,0.12)' : 'transparent',
                        color: activeTab === tab ? '#8B5CF6' : textSecondary,
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      {tab === 'plan' ? 'Plan' : 'DSL'}
                    </button>
                  ))}
                </div>
                <div
                  style={{
                    padding: '14px', borderRadius: 10,
                    background: cardBg, border: `1px solid ${cardBorder}`,
                    color: textSecondary,
                  }}
                >
                  {activeTab === 'plan' && result.plan
                    ? <PlanView plan={result.plan} />
                    : <DSLView dsl={result.dsl} isDark={isDark} />
                  }
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <div
        style={{
          padding: '14px 20px',
          borderTop: `1px solid ${border}`,
          flexShrink: 0,
        }}
      >
        <p style={{ fontSize: 11, color: textSecondary, textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
          Nodes are rendered automatically on the canvas.
          Edit configurations manually after generation, then click <strong>Save</strong>.
        </p>
      </div>

      <style>{`.spin { animation: spin 0.9s linear infinite; } @keyframes spin { to { transform: rotate(360deg); }}`}</style>
    </motion.div>
  );
}
